import { IconButton, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import type { MeterForProductPageResponse } from '@weft/api-definition';
import { VyIcon } from '@weft/icons';
import VyDialog from 'apps/app/src/components/new-design/feedback/VyDialog';
import { useDeleteMeter } from 'apps/app/src/hooks/requests/use-delete-meter';
import { useDialog } from 'apps/app/src/hooks/use-dialog';
import { useMenu } from 'apps/app/src/hooks/use-menu';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';

type MetersTableActionsProps = {
  meterId: string;
  row: MeterForProductPageResponse;
  disableDelete: boolean;
  meterName?: string;
};

export const MetersTableActions: React.FC<MetersTableActionsProps> = ({ meterId, row, disableDelete, meterName }) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const { anchorElement, isOpen, handleMenuOpen, handleMenuClose } = useMenu();

  const queryClient = useQueryClient();

  const deleteMeterMutation = useDeleteMeter({
    onSuccess: () => queryClient.invalidateQueries(['products-page']),
  });

  const handleAction = (actionFn?: () => void) => {
    handleMenuClose();
    actionFn?.();
  };

  const onDelete = async () => {
    await runMutation(
      deleteMeterMutation,
      { meterId },
      {
        pending: 'Deleting meter...',
        success: 'Meter successfully deleted',
        error: 'Failed to delete meter',
      });
  };

  return (
    <div> 
      <VyDialog
        open={isDialogOpen}
        onClose={handleClose}
        label={`Delete ${meterName}`}
        labelDescription="Are you sure you want to delete this metered product?"
        ctaText="Delete"
        warningText="This action is final and cannot be reversed"
        cancelButtonText="Cancel"
        onSubmit={onDelete}
      />
      <IconButton
        size="medium"
        onClick={handleMenuOpen}
        disabled={false}
      >
        <VyIcon
          icon="DotsMenu"
          style={{ transform: 'rotate(90deg)' }}
        />
      </IconButton>

      <Menu
        anchorEl={anchorElement}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={isOpen}
        onClose={handleMenuClose}
      >
        <Link
          to={`/products/${meterId}/edit`}
          state={row}
        >
          <MenuItem >
            <ListItemIcon>
              <VyIcon icon="Edit" />
            </ListItemIcon>
            <Typography
              fontSize={16}
              fontWeight={350}
            >
              Edit
            </Typography>
          </MenuItem>
        </Link>
        
        <MenuItem
          onClick={() => handleAction(handleOpen)}
          disabled={disableDelete}
        >
          <ListItemIcon>
            <VyIcon icon="Delete" />
          </ListItemIcon>
          <Typography
            fontSize={16}
            fontWeight={350}
          >
            Delete
          </Typography>
        </MenuItem>
      </Menu>
    </div>
  );
};